import { authKind } from "./share";

const SUPABASE_URL: string | undefined = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY: string | undefined = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const FLUSH_BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 30_000;
const HEARTBEAT_SECONDS: readonly number[] = [30, 120, 300];
const SIGNIN_DONE_KEY = "telemetry_signin_done_v1";

export type TelemetryEvent =
  | "page_view"
  | "audio_start"
  | "first_place"
  | "heartbeat"
  | "tutorial_done"
  | "tutorial_skip"
  | "community_open"
  | "scene_view"
  | "like"
  | "signin_start"
  | "signin_done"
  | "upload_click"
  | "upload_done";

const SESSION_ONCE_EVENTS: ReadonlySet<TelemetryEvent> = new Set(["audio_start", "first_place"]);

type TelemetryRow = { session_id: string; event: TelemetryEvent; props: Record<string, unknown> | null };

let enabled = false;
let debugLog = false;
let sessionId = "";
let buffer: TelemetryRow[] = [];
const sessionOnceFired = new Set<TelemetryEvent>();

function computeEnabled(params: URLSearchParams): boolean {
  if (params.has("telemetry")) {
    return params.get("telemetry") === "1";
  }
  return import.meta.env.PROD && !params.has("bench");
}

function refOrigin(): string | null {
  if (document.referrer === "") {
    return null;
  }
  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
}

async function emitPageView(): Promise<void> {
  const signedIn = await authKind();
  track("page_view", {
    ref: refOrigin(),
    lang: navigator.language,
    vw: window.innerWidth,
    vh: window.innerHeight,
    dpr: window.devicePixelRatio,
    mobile: matchMedia("(pointer: coarse)").matches,
    signed_in: signedIn,
  });
  if (signedIn === "real") {
    track("signin_done");
  }
}

function scheduleHeartbeats(): void {
  for (const sec of HEARTBEAT_SECONDS) {
    setTimeout((): void => track("heartbeat", { sec }), sec * 1000);
  }
}

function flush(): void {
  if (buffer.length === 0 || SUPABASE_URL === undefined || SUPABASE_PUBLISHABLE_KEY === undefined) {
    return;
  }
  const rows = buffer;
  buffer = [];
  if (debugLog) {
    console.debug("[telemetry] flush", rows);
  }
  fetch(`${SUPABASE_URL}/rest/v1/telemetry_events`, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_PUBLISHABLE_KEY,
      Prefer: "return=minimal",
    },
    body: JSON.stringify(rows),
  }).catch((): void => {});
}

/**
 * Boots telemetry for the current page load: resolves whether sending is enabled
 * (production only, unless overridden by ?telemetry=1 for dev verification), then
 * wires page_view, the three fixed-offset heartbeats, the periodic flush timer, and
 * a flush-on-hide listener. No-op when disabled so callers never branch on state.
 */
export function initTelemetry(): void {
  const params = new URLSearchParams(location.search);
  enabled = computeEnabled(params);
  if (!enabled) {
    return;
  }
  debugLog = import.meta.env.DEV && params.get("telemetry") === "1";
  sessionId = crypto.randomUUID();
  document.addEventListener("visibilitychange", (): void => {
    if (document.visibilityState === "hidden") {
      flush();
    }
  });
  setInterval((): void => {
    if (buffer.length > 0) {
      flush();
    }
  }, FLUSH_INTERVAL_MS);
  void emitPageView();
  scheduleHeartbeats();
}

/** Buffers a telemetry event; no-op when telemetry is disabled. Enforces the session/device once-guards for audio_start, first_place, and signin_done. */
export function track(event: TelemetryEvent, props?: Record<string, unknown>): void {
  if (!enabled) {
    return;
  }
  if (SESSION_ONCE_EVENTS.has(event)) {
    if (sessionOnceFired.has(event)) {
      return;
    }
    sessionOnceFired.add(event);
  }
  if (event === "signin_done") {
    if (localStorage.getItem(SIGNIN_DONE_KEY) === "1") {
      return;
    }
    localStorage.setItem(SIGNIN_DONE_KEY, "1");
  }
  if (debugLog) {
    console.debug("[telemetry] track", event, props);
  }
  buffer.push({ session_id: sessionId, event, props: props ?? null });
  if (buffer.length >= FLUSH_BATCH_SIZE) {
    flush();
  }
}
